import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const EXCEL_EXTENSION = '.xlsx';

@Injectable({
    providedIn: 'root'
})
export class ExcelService {

    private readonly DatosGeneralesTitle = 'DatosGenerales';
    private readonly PersonasRelacionadasTitle = 'PersonasRelacionadas';
    private readonly DatosDomicilioTitle = 'DatosDomicilio';
    private readonly MediosContactoTitle = 'MediosContacto';
    private readonly OperacionesTitle = 'Operaciones';
    private readonly OperacionesCanceladasTitle = 'OperacionesCanceladas';

    private readonly DatosGeneralesEndpoint = 'Persona';
    private readonly PersonasRelacionadasEndpoint = 'PersonaRelacionada';
    private readonly DatosDomiciliosEndpoint = 'Domicilio';
    private readonly MediosContactoEndpoint = 'MedioContacto';
    private readonly OperacionesEndpoint = 'Operacion';
    private readonly OperacionesCanceladasEndpoint = 'OperacionCancelada';
    
    private readonly DatosGeneralesEncabezados = [
        'ClienteId',
        'PersonalidadFiscalId',
        'Nombre',
        'SNombre',
        'ApellidoPaterno',
        'ApellidoMaterno',
        'FechaNacimientoConstitucion',
        'RFC',
        'CURP',
        'PaisNacimientoClave',
        'EstadoNacimientoId',
        'Nacionalidad',
        'ActividadEconomicaId',
        'GiroMercantilId',
        'Geolocalizacion',
        'IdentificadorFideicomiso',
        'SujetoObligado',
        'AlmacenClave',
        'FacturaReferencia',
        'FechaEmision',
        'TipoPersona'
    ];

    private readonly PersonasRelacionadasEncabezados = [
        'ClienteId',
        'PersonaRelacionadaId',
        'ParentescoId',
        'Nombre',
        'SNombre',
        'ApellidoPaterno',
        'ApellidoMaterno',
        'FechaNacimientoConstitucion',
        'RFC',
        'CURP',
        'PaisNacionalidadClave',
        'EstadoNacimientoId',
        'IdentificadorFideicomiso',
        'PorcentajeDesignado',
        'SujetoObligado',
        'AlmacenClave',
        'PersonalidadFiscalId',
        'FacturaReferencia',
        'FechaEmision',
        'TipoPersona',
        'PaisNacimientoClave',
        'Nacionalidad'
    ];

    private readonly DatosDomicilioEncabezados = [
        'ClienteId',
        'PaisClave',
        'Colonia',
        'Calle',
        'NumExterior',
        'NumInterior',
        'CodigoPostal',
        'EstadoExt',
        'CiudadPoblacion',
        'SujetoObligado',
        'AlmacenClave',
        'FacturaReferencia',
        'FechaEmision'
    ];

    private readonly MediosContactoEncabezados = [
        'ClienteId',
        'PaisClave',
        'MedioContactoId',
        'Contacto',
        'SujetoObligado',
        'AlmacenClave',
        'FacturaReferencia',
        'FechaEmision'
    ];

    private readonly OperacionesEncabezados = [
        'ClienteId',
        'TipoOperacionId',
        'FormaPagoId',
        'InstrumentoId',
        'DivisaId',
        'EstatusId',
        'Marca',
        'Modelo',
        'Anio',
        'Chasis',
        'NumInscripcion',
        'Placas',
        'NivelBlindaje',
        'ImporteFactura',
        'SubTotalsinIVA',
        'ImporteISAN',
        'ImporteIVA',
        'Recibo',
        'ImporteRecibo',
        'ReciboConsecutivo',
        'FechaPago',
        'FechaCancelacionRecibo',
        'BanderaId',
        'CanalVenta',
        'SujetoObligado',
        'AlmacenClave',
        'FacturaReferencia',
        'FechaEmision'
    ];

    private readonly OperacionesCanceladasEncabezados = [
        'ClienteId',
        'ImporteFactura',
        'Modelo',
        'Anio',
        'Chasis',
        'FechaCancelacion',
        'SujetoObligado',
        'AlmacenClave',
        'FacturaReferencia',
        'FechaEmision'
    ];

    private readonly hojas = [
        {
            titulo: this.DatosGeneralesTitle,
            endpoint: this.DatosGeneralesEndpoint,
            encabezados: this.DatosGeneralesEncabezados
        },
        {
            titulo: this.PersonasRelacionadasTitle,
            endpoint: this.PersonasRelacionadasEndpoint,
            encabezados: this.PersonasRelacionadasEncabezados
        },
        {
            titulo: this.DatosDomicilioTitle,
            endpoint: this.DatosDomiciliosEndpoint,
            encabezados: this.DatosDomicilioEncabezados
        },
        {
            titulo: this.MediosContactoTitle,
            endpoint: this.MediosContactoEndpoint,
            encabezados: this.MediosContactoEncabezados
        },
        {
            titulo: this.OperacionesTitle,
            endpoint: this.OperacionesEndpoint,
            encabezados: this.OperacionesEncabezados
        },
        {
            titulo: this.OperacionesCanceladasTitle,
            endpoint: this.OperacionesCanceladasEndpoint,
            encabezados: this.OperacionesCanceladasEncabezados
        }
    ]

    constructor() { }

    public exportLayout(endpoint: string, jsonRequest: string, fileName: string){
        let workbook: XLSX.WorkBook; 
        let data = JSON.parse(jsonRequest);
        data = this.prepararDatos(data, endpoint);
        
        const sheets : { [key: string]: XLSX.WorkSheet } = {};
        const sheetNames : string[] = [];

        this.hojas.forEach(h => {
            sheets[h.titulo] = endpoint === h.endpoint
                ? XLSX.utils.json_to_sheet([data])
                : XLSX.utils.aoa_to_sheet([h.encabezados]);

            sheetNames.push(h.titulo);
        });

        workbook = {
            Sheets: sheets,
            SheetNames: sheetNames 
        };

        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, fileName);
    }

    private prepararDatos(data: any, endpoint: string): any {
        const hojaActual = this.hojas.find(h => h.endpoint === endpoint);

        if (!hojaActual) {
            return data;
        }

        if (data.Cliente) {
            data.ActividadEconomicaId = data.Cliente.ActividadEconomicaId;
            data.GiroMercantilId = data.Cliente.GiroMercantilId;
            data.Geolocalizacion = data.Cliente.Geolocalizacion;
        }
        delete data.Cliente;

        if (data.PersonaRelacionada) {
            data.PersonaRelacionadaId = data.PersonaRelacionada.PersonaRelacionadaId;
            data.ParentescoId = data.PersonaRelacionada.ParentescoId;
            data.PaisNacionalidadClave = data.PersonaRelacionada.PaisNacionalidadClave;
            data.PorcentajeDesignado = data.PersonaRelacionada.PorcentajeDesignado;
        }
        delete data.PersonaRelacionada;

        return this.ordenar(data, hojaActual.encabezados);
    }

    private ordenar(data: any, campos: string[]): any {
        const resultado = {};

        campos.forEach(campo => {
            resultado[campo] = data[campo];
        });
        return resultado;
    }

    public exportAsExcelFile(json: any[], excelFileName: string, excelSheetName): void {
        let workbook: XLSX.WorkBook;
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);


        if (excelSheetName === 'Clientes') {
            workbook = { Sheets: { 'Clientes': worksheet }, SheetNames: ['Clientes'] };
        }

        if (excelSheetName === 'Empleados') {
            workbook = { Sheets: { 'Empleados': worksheet }, SheetNames: ['Empleados'] };
        }

        if (excelSheetName === 'PersonasRelacionadas') {
            workbook = { Sheets: { 'PersonasRelacionadas': worksheet }, SheetNames: ['PersonasRelacionadas'] };
        }

        if (excelSheetName === 'Coincidencias') {
            workbook = { Sheets: { 'Coincidencias': worksheet }, SheetNames: ['Coincidencias'] };
        }

        if (excelSheetName === 'Alertas') {
            workbook = { Sheets: { 'Alertas': worksheet }, SheetNames: ['Alertas'] };
        }


        if (excelSheetName === 'AlertasComite') {
            workbook = { Sheets: { 'AlertasComite': worksheet }, SheetNames: ['AlertasComite'] };
        }


        if (excelSheetName === 'Transacciones') {
            workbook = { Sheets: { 'Transacciones': worksheet }, SheetNames: ['Transacciones'] };
        }

        if (excelSheetName === 'Relevantes') {
            workbook = { Sheets: { 'Relevantes': worksheet }, SheetNames: ['Relevantes'] };
        }

        if (excelSheetName === 'Inusuales') {
            workbook = { Sheets: { 'Inusuales': worksheet }, SheetNames: ['Inusuales'] };
        }

        if (excelSheetName === 'InternasPreocupantes') {
            workbook = {
                Sheets: { 'Internas Preocupantes': worksheet }, SheetNames: ['Internas Preocupantes']
            };
        }

        if (excelSheetName === 'ClientesCalificados') {
            workbook = {
                Sheets:
                    { 'Clientes Calificados': worksheet }, SheetNames: ['Clientes Calificados']
            };
        }

        if (excelSheetName === 'EBR') {
            workbook = { Sheets: { 'EBR': worksheet }, SheetNames: ['EBR'] };
        }

        if (excelSheetName === 'BitacoraUsuario') {
            workbook = {
                Sheets:
                    { 'Bitácora de Usuarios': worksheet }, SheetNames: ['Bitácora de Usuarios']
            };
        }

        if (excelSheetName === 'BitacoraAcceso') {
            workbook = {
                Sheets: { 'Bitácora de Accesos': worksheet }, SheetNames: ['Bitácora de Accesos']
            };
        }

        if (excelSheetName === 'Física') {
            workbook = { Sheets: { 'Física': worksheet }, SheetNames: ['Física'] };
        }

        if (excelSheetName === 'Moral') {
            workbook = { Sheets: { 'Moral': worksheet }, SheetNames: ['Moral'] };
        }

        if (excelSheetName === 'ErroresCarga') {
            workbook = { Sheets: { 'ErroresCarga': worksheet }, SheetNames: ['ErroresCarga'] };
        }


        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        this.saveAsExcelFile(excelBuffer, excelFileName);
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
        FileSaver.saveAs(data, fileName + '_' + new Date().getTime() + EXCEL_EXTENSION);
    }
}
